import { DesignDialog as Dialog } from "../../design/DesignDialog.js";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { FUNCTION_PORT_VALUE_TYPES } from "../../../../util/src/blueprint/function-graph.js";
import { getValueTypeColor } from "../../../../util/src/blueprint/method.js";
import dico from "../../../../util/src/language/dico.js";
import LabelImportantIcon from "@mui/icons-material/LabelImportant";
import { useCallback, useMemo, useState } from "react";

const ValueTypeMenuIndicator = ({ valueType }: { valueType: string }) => {
  if (valueType === "flow") {
    return (
      <LabelImportantIcon
        sx={{ fontSize: 16, color: "#111111", flexShrink: 0 }}
      />
    );
  }
  return (
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: getValueTypeColor(valueType),
        flexShrink: 0,
      }}
    />
  );
};

export interface FunctionPortAddMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (valueType: string, label: string) => void;
}

/**
 * Two-step picker: value type menu, then label dialog for a new function port.
 */
export const FunctionPortAddMenu = ({
  anchorEl,
  open,
  onClose,
  onConfirm,
}: FunctionPortAddMenuProps) => {
  const behaviorDico = dico().behavior;
  const labels = behaviorDico.sceneCatalog.functionGraph;
  const inputTypeLabels = behaviorDico.nodeList.inputTypes as Record<
    string,
    string
  >;
  const valueTypes = useMemo(() => [...FUNCTION_PORT_VALUE_TYPES], []);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [label, setLabel] = useState("");

  const reset = useCallback(() => {
    setSelectedType(null);
    setLabel("");
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handlePickType = useCallback((valueType: string) => {
    setSelectedType(valueType);
  }, []);

  const handleConfirmLabel = useCallback(() => {
    if (!selectedType || !label.trim()) return;
    onConfirm(selectedType, label.trim());
    handleClose();
  }, [handleClose, label, onConfirm, selectedType]);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open && selectedType === null}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem disabled sx={{ opacity: 1, fontSize: 12, fontWeight: 600 }}>
          {labels.selectValueType}
        </MenuItem>
        {valueTypes.map((valueType) => (
          <MenuItem
            key={valueType}
            onClick={() => handlePickType(valueType)}
            sx={{ gap: 1, fontSize: 12 }}
          >
            <ValueTypeMenuIndicator valueType={valueType} />
            {inputTypeLabels[valueType] ?? valueType}
          </MenuItem>
        ))}
      </Menu>
      <Dialog
        open={selectedType !== null}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14 }}>{labels.portLabel}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={label}
            placeholder={labels.portLabelPlaceholder}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleConfirmLabel();
              }
            }}
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{labels.cancel}</Button>
          <Button
            variant="contained"
            onClick={handleConfirmLabel}
            disabled={!label.trim()}
          >
            {labels.confirmAddPort}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
